import { Operation } from "@/backend/models/operation";
import { useBlockStore } from "@/frontend/store/BlockStore";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { copyPasteTypes } from "@/types/copyPasteTypes";
import { ParameterSubjectType, ParameterInputType } from "@/types/parameterTypes";
import { useState, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import { I18n } from "@/i18n/I18n";
import { operationTypes } from "@/types/operationTypes";
import { Form } from "react-bootstrap";
import { useParseProcedureReferences } from "@/frontend/hooks/parameters";
import { DropdownMenu } from "../dropdown/DropdownMenu";
import { Template } from "../Template";
import { FieldItem } from "./FieldItem";
import { OperationButtonRow } from "./OperationButtonRow";
import { CollapsibleAddRow } from "../buttons/CollapsibleAddRow";
import { FileInputItem } from "./FileInputItem";
import { TextInput } from "./TextInput";
import { Container } from "@/backend/models/containers/container";
import { Block } from "@/backend/models/block";
import { NotesItem } from "./NotesItem";
import { OperationFileUploader } from "./OperationFileUploader";
import { CodeItem } from "../codes/CodeItem";
import { CommandingItem } from "./CommandingItem";
import { Parameter } from "@/backend/models/parameter";
import { GraphItem } from "./graphs/GraphItem";
import { EquipmentOperation } from "./EquipmentOperation";
import { CalloutIcon } from "./CalloutIcon";

export function OperationItem(props: {
  initItem: Operation;
  parent: Container | Block;
}) {
  const {
    initItem,
    parent
  } = props;
  const deleteOperation = useTreeStore((state) => state.deleteOperation);
  const duplicateOperation = useTreeStore((state) => state.duplicateOperation);
  const saveOperationAsBlock = useBlockStore((state) => state.saveOperationAsBlock);
  const orderOperations = useTreeStore((state) => state.orderOperations);
  const updateParameter = useParameterStore((state) => state.updateParameter);
  const parameterIdentifierInvalid = useParameterStore((state) => state.parameterIdentifierInvalid);
  const operation = initItem;
  const { getStoreOperationsForParent: getStoreOperationsForStep } = useTreeStore.getState();
  const operationsInStep = getStoreOperationsForStep(operation.parentId);
  const [parameter] = useParameterStore(useShallow(state => state.parametersForSubject(operation.id, ParameterSubjectType.OPERATION)));
  const updateOperation = useTreeStore((state) => state.updateOperation);
  const idForScroll = useTreeStore((state) => state.idForScroll);
  const idForScrollFound = useTreeStore((state) => state.idForScrollFound);
  const setIdForScrollFound = useTreeStore((state) => state.setIdForScrollFound);
  const [showOperation, setShowOperation] = useState(true);
  const [title, setTitle] = useState(operation.title);
  const [error, setError] = useState('');
  const [identifier, setIdentifier] = useState(parameter?.identifier);
  const [identifierError, setIdentifierError] = useState('');
  const parseProcedureReferences = useParseProcedureReferences(operation);

  useEffect(() => {
    setTitle(operation.title);
  }, [operation.title]);

  useEffect(() => {
    setIdentifier(parameter?.identifier);
  }, [parameter?.identifier]);

  const classForType = () => {
    if (operation.type === operationTypes.Warning) return 'bg-warning-subtle border border-warning p-1 rounded';
    if (operation.type === operationTypes.Danger) return 'bg-danger-subtle border border-danger p-1 rounded';
    if (operation.type === operationTypes.Information) return 'bg-info-subtle border border-info p-1 rounded';
    return '';
  };

  const onChangeTitle = (value: string) => {
    setTitle(value);
    setError(parseProcedureReferences(value) || '');
  };

  const onChangeIdentifier = (value: string) => {
    setIdentifier(value);
    setIdentifierError(parameterIdentifierInvalid(new Parameter({ ...parameter, identifier: value })) || '');
  };

  const onBlurIdentifier = () => {
    if (identifierError) return;
    void updateParameter({ parameter: { ...parameter, identifier } });
  };

  useEffect(() => {
    if (idForScroll === operation.id && !idForScrollFound) {
      setIdForScrollFound(true);
    }
  }, [idForScroll, operation.id, setIdForScrollFound]);

  return (
    <span key={operation.id}>
      <hr />
      <div id={operation.id} className='d-flex flex-column observed-element'>
        <div className="d-flex justify-content-between w-100">
          <div>
            <CollapseButton
              showElement={showOperation}
              setShowElement={setShowOperation}
              title={I18n.get(operation.type)}
            />
            <small className={`text-muted text-uppercase d-inline-flex align-items-center gap-1 lh-1 ${classForType()}`}>
              <CalloutIcon type={operation.type} size={14} />
              <label aria-label={I18n.get(operation.type)} htmlFor={operation.id}>
                {I18n.get(operation.type)}
              </label>
            </small>
          </div>
          <DropdownMenu
            onClickDupe={() => void duplicateOperation(operation.id)}
            saveAsBlock={(title) => void saveOperationAsBlock(operation, title)}
            instance={copyPasteTypes.Operation}
            orderItems={(operations) => orderOperations({ operations })}
            onClickDelete={() => deleteOperation(operation)}
            listOfItems={operationsInStep}
            item={operation}
          />
        </div>
        <div className='d-flex gap-2 w-100'>
          <div className='flex-fill'>
            <Form.Label className="col-form-label col-form-label-sm">{I18n.get('Title')}</Form.Label>
            <Form.Control 
              type="text"
              size='sm' 
              value={title} 
              onChange={(e) => onChangeTitle(e.target.value)} 
              isInvalid={!!error}
              onBlur={() => void updateOperation({ operation: { ...operation, title } })} 
            />
            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
          </div>
          {operation.hasIdentifier() && <div className='flex-fill'>
            <Form.Label className="col-form-label col-form-label-sm">{I18n.get('identifier')}</Form.Label>
            <Form.Control 
              type="text"
              size='sm' 
              value={identifier} 
              onChange={(e) => onChangeIdentifier(e.target.value)} 
              isInvalid={!!identifierError}
              onBlur={onBlurIdentifier} 
            />
            {identifierError && <Form.Control.Feedback type="invalid">{identifierError}</Form.Control.Feedback>}
          </div>}
        </div>
        {operation.isScaffold && parameter?.inputType !== ParameterInputType.TABLE && <div className='w-100'><Template initItem={operation} updateItem={(item) => updateOperation({ operation: item })} /></div>}
        <div className='w-100' hidden={!showOperation}>
          {operation.isField() && <FieldItem operation={operation} />}
          {operation.isFieldInput() && <FieldItem operation={operation} />}
          {operation.isTable() && <FieldItem operation={operation} />}

          {operation.isFile() && <OperationFileUploader operation={operation} />}
          {operation.isFileInput() && <FileInputItem operation={operation}/>}
          {operation.isText() && <TextInput operation={operation} />}
          {operation.isNotes() && <NotesItem operation={operation} />}
          {operation.isCode() && <CodeItem operation={operation} />}
          {operation.isCommanding() && <CommandingItem operation={operation}/>}
          {operation.isGraph() && <GraphItem operation={operation} />}
          {operation.isEquipment() && <EquipmentOperation operation={operation} />}
        </div>
      </div>
      <div className="mt-1">
        <CollapsibleAddRow label={I18n.get('addOperationHere')}>
          <OperationButtonRow position={operation.position + 1} parent={parent}/>
        </CollapsibleAddRow>
      </div>
    </span>
  );
}
